import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { RecipeCard } from '../components/RecipeCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { categoryFilters, getCategoryLabel } from '../lib/categories';

const difficulties = [
  { id: 'alle', name: 'Alle Schwierigkeiten' },
  { id: 'einfach', name: 'Einfach' },
  { id: 'mittel', name: 'Mittel' },
  { id: 'schwer', name: 'Schwer' },
];

const times = [
  { id: 'alle', name: 'Alle Zeiten' },
  { id: '15', name: 'Unter 15 Min' },
  { id: '30', name: 'Unter 30 Min' },
  { id: '60', name: 'Unter 60 Min' },
];

export const Recipes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  const [search, setSearch] = useState(searchParams.get('suche') || '');
  const [category, setCategory] = useState(searchParams.get('kategorie') || 'alle');
  const [difficulty, setDifficulty] = useState(searchParams.get('schwierigkeit') || 'alle');
  const [time, setTime] = useState(searchParams.get('zeit') || 'alle');

  useEffect(() => {
    const fetchRecipes = async () => {
      setLoading(true);
      try {
        let q = query(collection(db, 'recipes'), orderBy('createdAt', 'desc'));
        
        // Note: Firestore has limitations with complex queries
        // For production, consider using Algolia or similar for search
        const snapshot = await getDocs(q);
        let allRecipes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Client-side filtering
        if (category && category !== 'alle') {
          allRecipes = allRecipes.filter(r => r.category === category);
        }
        
        if (difficulty && difficulty !== 'alle') {
          allRecipes = allRecipes.filter(r => r.difficulty === difficulty);
        }
        
        if (time && time !== 'alle') {
          const maxTime = parseInt(time);
          allRecipes = allRecipes.filter(r => r.prepTime && r.prepTime <= maxTime);
        }
        
        if (search) {
          const searchLower = search.toLowerCase();
          allRecipes = allRecipes.filter(r => 
            r.title?.toLowerCase().includes(searchLower) ||
            r.description?.toLowerCase().includes(searchLower) ||
            r.ingredients?.some(i => i.toLowerCase().includes(searchLower))
          );
        }
        
        setRecipes(allRecipes);
      } catch (error) {
        console.error('Error fetching recipes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipes();
  }, [category, difficulty, time, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    updateParams({ suche: search });
  };

  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'alle') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('alle');
    setDifficulty('alle');
    setTime('alle');
    setSearchParams({});
  };

  const hasActiveFilters = search || category !== 'alle' || difficulty !== 'alle' || time !== 'alle';

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-navy mb-2">
            Alle Rezepte
          </h1>
          <p className="text-olive">
            {loading ? 'Lade Rezepte...' : `${recipes.length} Rezepte gefunden`}
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
              <Input
                type="text"
                placeholder="Rezept oder Zutat suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 py-5 rounded-xl bg-white shadow-sm"
                data-testid="recipes-search-input"
              />
            </div>
            <Button 
              type="submit"
              className="bg-terracotta hover:bg-terracotta/90 text-white rounded-xl px-6"
              data-testid="recipes-search-btn"
            >
              Suchen
            </Button>
            <Button 
              type="button"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="rounded-xl px-4 md:hidden"
            >
              <SlidersHorizontal className="w-5 h-5" />
            </Button>
          </form>

          {/* Desktop Filters */}
          <div className={`${showFilters ? 'block' : 'hidden'} md:flex flex-wrap gap-4 items-center`}>
            <Select value={category} onValueChange={(v) => { setCategory(v); updateParams({ kategorie: v }); }}>
              <SelectTrigger className="w-full md:w-48 rounded-xl bg-white" data-testid="filter-category">
                <SelectValue placeholder="Kategorie" />
              </SelectTrigger>
              <SelectContent>
                {categoryFilters.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={difficulty} onValueChange={(v) => { setDifficulty(v); updateParams({ schwierigkeit: v }); }}>
              <SelectTrigger className="w-full md:w-48 rounded-xl bg-white" data-testid="filter-difficulty">
                <SelectValue placeholder="Schwierigkeit" />
              </SelectTrigger>
              <SelectContent>
                {difficulties.map(diff => (
                  <SelectItem key={diff.id} value={diff.id}>{diff.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={time} onValueChange={(v) => { setTime(v); updateParams({ zeit: v }); }}>
              <SelectTrigger className="w-full md:w-48 rounded-xl bg-white" data-testid="filter-time">
                <SelectValue placeholder="Zeit" />
              </SelectTrigger>
              <SelectContent>
                {times.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                onClick={clearFilters}
                className="text-terracotta hover:text-terracotta/80 gap-1"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4" /> Filter löschen
              </Button>
            )}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-3xl h-80 animate-pulse" />
            ))}
          </div>
        ) : recipes.length > 0 ? (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {recipes.map((recipe, index) => (
              <RecipeCard key={recipe.id} recipe={recipe} index={index} />
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-bone-alt rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-olive" />
            </div>
            <h3 className="font-display text-xl font-semibold text-navy mb-2">
              Keine Rezepte gefunden
            </h3>
            <p className="text-olive mb-6">
              Versuche andere Suchbegriffe oder Filter.
            </p>
            {hasActiveFilters && (
              <Button 
                onClick={clearFilters}
                className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6"
              >
                Filter zurücksetzen
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
